import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i);
  return output;
}

export function usePush() {
  const [state, setState] = useState({
    supported: false,
    permission: 'default',
    subscribed: false,
    loading: false,
  });

  useEffect(() => {
    // Check support
    const ok = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    if (!ok) {
      console.log('[Push] Not supported in this browser');
      return;
    }

    const perm = Notification.permission;
    setState(s => ({ ...s, supported: true, permission: perm }));

    // SW is already registered globally in index.html — just wait for it to be ready
    navigator.serviceWorker.ready
      .then(reg => reg.pushManager.getSubscription())
      .then(sub => {
        setState(s => ({ ...s, subscribed: !!sub }));
      })
      .catch(() => {});
  }, []);

  const requestAndSubscribe = async () => {
    setState(s => ({ ...s, loading: true }));
    try {
      // Request permission if not yet granted
      let perm = Notification.permission;
      if (perm !== 'granted') {
        perm = await Notification.requestPermission();
        setState(s => ({ ...s, permission: perm }));
      }
      if (perm !== 'granted') {
        console.log('[Push] Permission denied');
        setState(s => ({ ...s, loading: false }));
        return false;
      }

      // Get VAPID key
      const { data } = await axios.get(`${API}/push/vapid-public`);
      console.log('[Push] VAPID key received');

      // Wait for SW
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });

      // Send to backend
      await axios.post(`${API}/push/subscribe`, sub.toJSON());
      console.log('[Push] Subscribed successfully');
      setState(s => ({ ...s, subscribed: true, permission: 'granted', loading: false }));
      return true;
    } catch (err) {
      console.error('[Push] Subscribe error:', err);
      setState(s => ({ ...s, permission: Notification.permission, loading: false }));
      return false;
    }
  };

  const unsubscribe = async () => {
    setState(s => ({ ...s, loading: true }));
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setState(s => ({ ...s, subscribed: false, loading: false }));
    } catch (err) {
      setState(s => ({ ...s, loading: false }));
    }
  };

  return { ...state, requestAndSubscribe, unsubscribe };
}
